// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { spacing, typography } from "@recidiviz/design-system";
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  OnChangeFn,
  PaginationState,
  SortDirection,
  SortingState,
  TableState,
  useReactTable,
} from "@tanstack/react-table";
import { observer } from "mobx-react-lite";
import { rem } from "polished";
import { Dispatch, SetStateAction, useState } from "react";
import styled from "styled-components";

import { palette } from "~design-system";

import SortIcon from "../../assets/static/images/sortIcon.svg?react";
import useIsMobile from "../../hooks/useIsMobile";
import { NavigateToFormButtonStyle } from "../../WorkflowsStore/Opportunity/Forms/NavigateToFormButton";
import { Link } from "../Link";
import { StatusAwareButton } from "../OpportunityDenial/MenuButton.styles";
import { PersonIdWithCopyIcon } from "../PersonId/PersonId";
import { EnableToggle } from "../UsNcRNA/UsNcRNATable";

const Table = styled.table`
  width: 100%;
  ${typography.Sans14};
  text-align: left;

  border-collapse: separate;
  border-spacing: 0;
  flex: 1 1 auto;
`; // necessary to keep the border at the bottom of the sticky table header

const ScrollContainer = styled.div`
  overflow-x: auto;
  max-width: 100%;
  -webkit-overflow-scrolling: touch;
`;

const SortableHeader = styled.div<{ $sortable?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${rem(spacing.sm)};
  opacity: 100%;
  cursor: ${({ $sortable }) => ($sortable ? "pointer" : "default")};
`;

const TableHeader = styled.thead`
  position: sticky;
  top: 0;
  margin: 0;
  width: 100%;
  opacity: 100%;
  background-color: ${palette.marble1};
`;

const SharedTableCellStyles = `
  height: 49px;
  padding: ${rem(spacing.xs)} ${rem(spacing.sm)};
  border-bottom: 1px solid ${palette.slate10};
`;

const HeaderCell = styled.th`
  ${SharedTableCellStyles}
  color: ${palette.slate80};
  font-weight: unset;
  user-select: none;
`;

const Cell = styled.td<{
  $expandedLastColumn: boolean;
  $isMobile: boolean;
  $numColumns: number;
  $smallColumns: boolean;
  $shouldAppearUnselectable: boolean;
}>`
  ${SharedTableCellStyles}

  color: ${palette.pine1};

  ${({ $numColumns, $smallColumns }) =>
    /* If there are more than 7 columns, make columns accordingly narrower */
    !$smallColumns &&
    ($numColumns > 7 ? `width: calc(91% / ${$numColumns});` : `width: 13%;`)}

  ${({ $isMobile }) => ($isMobile ? `min-width: 100px;` : `min-width: 125px;`)}
  ${({ $smallColumns }) => $smallColumns && `min-width: 10px;`}

  ${({ $expandedLastColumn }) =>
    /* Offset last column to fill all available space */
    $expandedLastColumn &&
    `&:last-child {
    width: auto;
  }`}

  ${NavigateToFormButtonStyle} {
    min-height: 0;
    height: 32px;
    white-space: nowrap;
  }

  ${({ $shouldAppearUnselectable }) =>
    $shouldAppearUnselectable && `color: ${palette.slate50};`}

  ${PersonIdWithCopyIcon} {
    ${({ $shouldAppearUnselectable }) =>
      $shouldAppearUnselectable
        ? `color: ${palette.slate50}`
        : `color: ${palette.pine3}`};

    :hover,
    :focus {
      background-color: ${palette.marble5};
    }
    /* Increase size of target for click-to-copy when within a table cell */
    padding-top: ${rem(spacing.sm + spacing.xs)};
    padding-bottom: ${rem(spacing.sm + spacing.xs)};
  }

  &:has(${EnableToggle}) {
    border-left: 1px solid ${palette.slate10};
  }
`;

const Row = styled.tr<{
  $isSelected?: boolean;
  $shouldAppearUnselectable?: boolean;
}>`
  ${Cell} {
    ${({ $isSelected }) => $isSelected && "background-color: #EDF4FC;"}
    transition: all 0.15s ease-in-out;
    ${({ $shouldAppearUnselectable }) =>
      $shouldAppearUnselectable &&
      `color: ${palette.slate60}; cursor: not-allowed`}
  }

  // Only show UsAzMarkSubmittedButton and NavigateToFormButton buttons on row hover
  &:not(:hover, :focus) {
    ${NavigateToFormButtonStyle}, ${StatusAwareButton} {
      display: none;
    }
  }
`;

const TableBody = styled.tbody<{ $clickableRows: boolean }>`
  width: 100%;

  /* Give the hover state to only body rows, not header rows */
  & ${Row} {
    &:hover,
    &:focus {
      background-color: ${palette.marble2};
      cursor: ${({ $clickableRows }) =>
        $clickableRows ? "pointer" : "default"};
    }
  }
`;

const svgPathFillCSS = `
  fill-opacity: 1 !important;
  fill: ${palette.pine3}
`;

const SortIconWrapper = styled.div<{
  $sortDirection: false | SortDirection;
}>`
  color: ${palette.slate30};

  ${({ $sortDirection }) => {
    if ($sortDirection === "asc") {
      return `
          svg > path:first-child {
            ${svgPathFillCSS}
          }
        `;
    }
    if ($sortDirection === "desc") {
      return `
          svg > path:last-child {
            ${svgPathFillCSS}
          }
        `;
    }
    return "";
  }};
`;

const LoadMoreRows = styled.button`
  ${typography.Sans18};
  width: fit-content;
  display: flex;
  justify-content: center;
  align-items: center;
  color: ${palette.pine4};
  margin: ${rem(spacing.md)} auto ${rem(spacing.xl)} auto;
  border: none;
  background-color: transparent;

  &:hover {
    cursor: pointer;
  }
`;

// When `rowLinkUrl` is provided, the row's <td> children are wrapped in this
// anchor. `display: contents` keeps the <a> from breaking table layout while
// still surfacing each row as a real anchor — preserving right-click "Open in
// new tab" and keyboard navigation.
const RowLink = styled(Link)`
  display: contents;
  color: inherit;
  text-decoration: none;

  &:focus-visible td {
    outline: 2px solid ${palette.signal.links};
    outline-offset: -2px;
  }
`;

export type CaseloadTableManualSorting = {
  sorting: SortingState;
  setSorting: Dispatch<SetStateAction<SortingState>>;
};

export type CaseloadTableProps<TData> = {
  data: TData[];
  columns: ColumnDef<TData>[];
  expandedLastColumn?: boolean;
  onRowClick?: (row: TData) => void;
  /**
   * When provided, each row is wrapped in a `<Link>` to the returned URL.
   * This makes the row a real anchor (so right-click "Open in new tab" and
   * keyboard navigation work), and is mutually compatible with `onRowClick`
   * — callers can use either or both. When omitted, behaviour is unchanged.
   */
  rowLinkUrl?: (row: TData) => string;
  shouldHighlightRow?: (row: TData) => boolean;
  onRowRender?: (row: TData) => void;
  manualSorting?: CaseloadTableManualSorting;
  enableMultiSort?: boolean;
  initialState?: Partial<TableState>;
  enableProgressiveLoading?: boolean;
  progressiveLoadingBatchSize?: number;
  /**
   * Splits the rows into pages. The caller owns the page state and draws the
   * page controls. Sorting still orders every row, not just the current page,
   * and the table goes back to the first page when the data changes. Cannot
   * be combined with enableProgressiveLoading.
   */
  pagination?: {
    state: PaginationState;
    onChange: OnChangeFn<PaginationState>;
  };
  /**
   * When provided true - this parameter overrides the standard styling
   * to prioritize smaller columns with a min-width of 10px & overwriting
   * min-width of 100px of 125px (mobile and laptop breakpoints respectively)
   */
  smallColumns?: boolean;
  shouldAppearUnselectable?: (row: TData) => boolean;
};

export const CaseloadTable = observer(function CaseloadTable<TData>({
  expandedLastColumn = false,
  data,
  columns,
  onRowClick,
  rowLinkUrl,
  shouldAppearUnselectable = () => false,
  shouldHighlightRow = () => false,
  onRowRender = () => undefined,
  manualSorting = undefined,
  enableMultiSort = false,
  initialState = undefined,
  enableProgressiveLoading = false,
  progressiveLoadingBatchSize = 0,
  pagination = undefined,
  smallColumns = false,
}: CaseloadTableProps<TData>) {
  if (enableProgressiveLoading && pagination) {
    throw new Error(
      "CaseloadTable cannot use enableProgressiveLoading and pagination together",
    );
  }

  const { isMobile } = useIsMobile(true);

  /*
   * NOTE: When progressive loading is enabled, the column sorting will sort the entire dataset
   *       and not exclusively the rows that are currently displayed.
   */
  const [progressiveLoading, setProgressiveLoading] = useState({
    pageIndex: 0,
    pageSize: progressiveLoadingBatchSize,
  });

  const table = useReactTable({
    data,
    columns: columns,
    getSortedRowModel: getSortedRowModel(),
    getCoreRowModel: getCoreRowModel(),
    ...(manualSorting
      ? {
          manualSorting: true,
          onSortingChange: manualSorting.setSorting,
        }
      : {}),
    ...(enableMultiSort
      ? {
          enableMultiSort: true,
          isMultiSortEvent: (_) => true,
          maxMultiSortColCount: 3,
        }
      : {}),
    ...(enableProgressiveLoading || pagination
      ? { getPaginationRowModel: getPaginationRowModel() }
      : {}),
    ...(pagination ? { onPaginationChange: pagination.onChange } : {}),
    initialState,
    state: {
      ...(manualSorting ? { sorting: manualSorting?.sorting } : {}),
      ...(enableProgressiveLoading ? { pagination: progressiveLoading } : {}),
      ...(pagination ? { pagination: pagination.state } : {}),
    },
  });

  const showLoadMoreButton =
    enableProgressiveLoading &&
    progressiveLoading.pageSize &&
    data.length > progressiveLoading.pageSize;

  const loadMoreRows = () => {
    setProgressiveLoading((prev) => ({
      ...prev,
      pageSize: prev.pageSize + progressiveLoadingBatchSize,
    }));
  };

  return (
    <ScrollContainer>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <Row key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <HeaderCell key={header.id}>
                  <SortableHeader
                    $sortable={header.column.getCanSort()}
                    onClick={header.column.getToggleSortingHandler()}
                  >
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}
                    {header.column.getCanSort() && (
                      <SortIconWrapper
                        $sortDirection={header.column.getIsSorted()}
                      >
                        <SortIcon />
                      </SortIconWrapper>
                    )}
                  </SortableHeader>
                </HeaderCell>
              ))}
            </Row>
          ))}
        </TableHeader>
        <TableBody $clickableRows={!!onRowClick || !!rowLinkUrl}>
          {table.getRowModel().rows.map((row) => {
            onRowRender(row.original);
            const cells = row.getVisibleCells().map((cell) => (
              <Cell
                key={cell.id}
                className={"fs-exclude"}
                $expandedLastColumn={expandedLastColumn}
                $isMobile={isMobile}
                $numColumns={columns.length}
                $smallColumns={smallColumns}
                $shouldAppearUnselectable={shouldAppearUnselectable(
                  row.original,
                )}
              >
                {flexRender(cell.column.columnDef.cell, cell.getContext())}
              </Cell>
            ));

            return (
              <Row
                key={row.id}
                onClick={() => {
                  if (onRowClick) onRowClick(row.original);
                }}
                $isSelected={shouldHighlightRow(row.original)}
                $shouldAppearUnselectable={shouldAppearUnselectable(
                  row.original,
                )}
              >
                {rowLinkUrl ? (
                  <RowLink to={rowLinkUrl(row.original)}>{cells}</RowLink>
                ) : (
                  cells
                )}
              </Row>
            );
          })}
        </TableBody>
      </Table>
      {showLoadMoreButton && (
        <LoadMoreRows onClick={loadMoreRows}>Load more</LoadMoreRows>
      )}
    </ScrollContainer>
  );
});
